// @flow

import { useRef } from 'react'

import { useSessionHeartbeat } from './useSessionHeartbeat'
import { ANALYTICS_EVENT } from '../const'

import type { SessionContent, UseSessionHeartbeatReturn } from './useSessionHeartbeat'

const VOD_TYPES = ['CATCHUP', 'MOVIE', 'TV_SHOW']

export type HeartbeatContentType = '' | 'TV' | 'RADIO' | 'CATCHUP' | 'MOVIE' | 'TV_SHOW'

export type HeartbeatProgress = {
  position: number,
  duration: number,
  speed: number,
}

export type HeartbeatTvShow = {
  id: string,
  title: string,
  seasonNumber: number,
  episodeNumber: number,
}
export type HeartbeatChannel = {
  id: string,
  title: string,
}

export type UsePlaybackHeartbeatOptions = {|
  contentType: HeartbeatContentType,
  contentId: string,
  contentTitle: string,
  channel?: ?HeartbeatChannel,
  tvShow?: ?HeartbeatTvShow,
  getProgress?: () => HeartbeatProgress,
  intervalMs?: number,
|}

export type UsePlaybackHeartbeatReturn = UseSessionHeartbeatReturn

export const usePlaybackHeartbeat = ({
  contentType,
  contentId,
  contentTitle,
  getProgress,
  tvShow,
  channel,
  intervalMs,
}: UsePlaybackHeartbeatOptions): UsePlaybackHeartbeatReturn => {
  const getProgressRef = useRef(getProgress)
  getProgressRef.current = getProgress

  const tvShowRef = useRef(tvShow)
  tvShowRef.current = tvShow

  const channelRef = useRef(channel)
  channelRef.current = channel

  const sessionTvShowRef = useRef(null)
  const sessionChannelRef = useRef(null)

  const onSessionStart = () => {
    sessionTvShowRef.current = tvShowRef.current
    sessionChannelRef.current = channelRef.current
  }

  // eslint-disable-next-line complexity
  const buildExtraPayload = (content: SessionContent): Object => {
    const extra: Object = {}

    if (VOD_TYPES.includes(content.contentType) && getProgressRef.current) {
      const { position, duration, speed } = getProgressRef.current()

      extra.playback_position = Math.trunc(position) || 0
      extra.content_duration = Math.trunc(duration) || 0

      if (speed && speed !== 1) {
        extra.playback_speed = speed
      }
    }

    if (content.contentType === 'TV_SHOW' && sessionTvShowRef.current) {
      const sessionTvShow = sessionTvShowRef.current

      extra.tv_show_id = sessionTvShow.id
      extra.tv_show_title = sessionTvShow.title
      extra.tv_show_season_number = sessionTvShow.seasonNumber
      extra.tv_show_episode_number = sessionTvShow.episodeNumber
    }

    if (content.contentType === 'CATCHUP' && sessionChannelRef.current) {
      const sessionChannel = sessionChannelRef.current

      extra.channel_id = sessionChannel.id
      extra.channel_title = sessionChannel.title
    }

    return extra
  }

  return useSessionHeartbeat({
    eventName: ANALYTICS_EVENT.PLAYBACK_HEARTBEAT,
    contentType,
    contentId,
    contentTitle,
    buildExtraPayload,
    onSessionStart,
    intervalMs,
  })
}

export default usePlaybackHeartbeat
